// 🧹 장표 글자 렌더 전처리(#6120) — 저장 데이터는 그대로 두고 «그릴 때»만 바꾼다.
//   ① 화면이 ✕·✓ 를 붙이는 칸(체크리스트 항목·비교 장표 두 제목)에서 글 앞머리의 같은 뜻 기호(❌·✅·✔·✖·☑…)를 뗀다
//      — 연출 AI 가 «❌ 잘못된 대응»처럼 쓰면 «✕❌ 잘못된 대응»으로 아이콘이 두 번 보였다(설계도 세 가족 재현).
//   ② 숫자+단위 덩이(«2,000만 원»·«13만 2천 원»)를 줄이 못 가르는 한 덩이로 그린다 — keep-all 은 띄어쓰기에서 줄을 갈라
//      «2,000만 / 원»·«13만 / 2천 원»이 났다.
//   ③ 수치(statRow) 값 글자 크기를 칸 폭에 맞춘다 — 값이 길면 칸이 글에 밀려 넓어져 화면 밖으로 나갔다.
//   ★붙임 문자(U+00A0·U+2060)로 풀지 않는다★ — 내보내기 엔진(@remotion/web-renderer 4.0.397 `findLineBreaks`)은 글을
//     `Intl.Segmenter` 낱말 단위로 하나씩 늘려 높이가 바뀌는 순간을 줄 끝으로 본다. 브라우저가 붙인 덩이를 통째로 다음 줄로
//     넘기면 엔진은 «덩이 중간»을 줄 끝으로 적어 첫 줄이 칸 밖으로 넘쳤다(10-09 스틸 실측 «7/10/15/20 / 년»).
//     그래서 덩이는 줄바꿈 없는 따로 상자(inline-block · nowrap)로, 수치 값은 한 줄 nowrap 또는 «직접 나눈 줄»로 그린다.
import { BRIEF_WIDTH_SLACK, briefTextWidth } from './motion-brief-shape';

const CHECK_MARKS = '✅✔✓☑🗸';
const CROSS_MARKS = '❌✖✕✗✘❎🚫⛔';

export type SlideMarkKind = 'check' | 'cross';

const markRe = (marks: string) => new RegExp(`^\\s*(?:[${marks}]\\uFE0F?[\\s:：·\\-–—]*)+`, 'u');
const MARK_RE: Readonly<Record<SlideMarkKind, RegExp>> = { check: markRe(CHECK_MARKS), cross: markRe(CROSS_MARKS) };

/** 화면이 이미 `kind` 표식을 붙이는 칸의 글에서 앞머리 같은 뜻 기호를 뗀다. 떼고 나면 빈 글이면 원문 그대로(표식만 있는 항목은 그대로 보인다). */
export function stripLeadingMark(text: string, kind: SlideMarkKind): string {
  const s = String(text ?? '');
  const m = MARK_RE[kind].exec(s);
  if (!m) return s;
  const rest = s.slice(m[0].length);
  return rest.trim() ? rest : s;
}

/** 숫자 뒤에 띄어 쓰여도 한 덩이로 보는 단위. */
const UNIT = '(?:원|달러|만|천|백|억|조|명|개월|개|년|영업일|일|시간|분|초|%|퍼센트|점|배|회|건|곳|살|세|평|위|등|번|가지|주|달)';
/** 숫자 덩이 = 숫자(콤마·소수) + (모션 무대 전처리가 넣은 U+2060) + 붙은 한글·% + (띄어쓰기 + 숫자… | 띄어쓰기 + 단위…)*. 한글이나 띄어쓰기가 든 덩이만 쓴다(«2024» 같은 맨 숫자는 그대로). */
const NUM_RUN = new RegExp(`\\d(?:[\\d,.]*\\d)?\\u2060?[가-힣%]*(?:[ \\u00A0]+(?:\\d(?:[\\d,.]*\\d)?\\u2060?[가-힣%]*|${UNIT}[가-힣]*))*`, 'gu');

export interface TextPiece { text: string; run: boolean }

/** 글을 «숫자+단위 덩이(run)»와 나머지로 가른다. 덩이가 없으면 조각 하나(run:false) — 호출부는 종전처럼 글 그대로 그린다. */
export function splitNumberRuns(text: string): TextPiece[] {
  const s = String(text ?? '');
  const out: TextPiece[] = [];
  let last = 0;
  for (const m of s.matchAll(NUM_RUN)) {
    if (!/[ \u00A0가-힣]/.test(m[0])) continue;
    const at = m.index ?? 0;
    if (at > last) out.push({ text: s.slice(last, at), run: false });
    out.push({ text: m[0], run: true });
    last = at + m[0].length;
  }
  if (last < s.length || out.length === 0) out.push({ text: s.slice(last), run: false });
  return out;
}

/** 수치 값의 그리기 계획 — `fit` 이 false 면 종전 그대로 그린다(바이트 동일). `lines` 는 줄마다 줄바꿈 없이 그린다. */
export type StatValuePlan = { fit: false } | { fit: true; lines: string[]; fontPx: number };

/** 굵은 숫자 폭 어림 — 글자 폭 모델(em) × 4% 여유. Paperlogy·Noto Sans KR 800 실측(10-09 · 5종)은 모두 em 이하(가장 빠듯 −1.4%). */
const valueWidth = (t: string, px: number) => (briefTextWidth(t, px) / BRIEF_WIDTH_SLACK) * 1.04;

/**
 * 수치 값이 칸 폭 `colW`(px) 안에 들어가게 글자 크기·줄을 정한다.
 *   - 종전 크기(`heroPx`)로 한 줄에 들어가면 손대지 않는다(`fit:false` · 종전 HTML 그대로).
 *   - 아니면 한 줄로 들어가는 크기까지 줄인다(하한 `floorPx`).
 *   - 하한으로도 한 줄이 안 되면 하한 크기에서 덩이(숫자+단위는 한 덩이) 사이로 줄을 직접 나눈다 — 가장 긴 덩이가 칸을 넘으면 그 덩이에 맞춰 더 줄인다.
 */
export function planStatValue(value: string, colW: number, heroPx: number, floorPx: number): StatValuePlan {
  const raw = String(value ?? '').trim();
  if (!raw || colW <= 0 || valueWidth(raw, heroPx) <= colW) return { fit: false };
  const one = Math.floor(colW / valueWidth(raw, 1));
  if (one >= floorPx) return { fit: true, lines: [raw], fontPx: Math.min(heroPx, one) };
  // 덩이 = 숫자 덩이 안은 안 가르고, 그 밖의 띄어쓰기에서만 가른다(«7/10/15/» 와 «20년» 처럼 붙은 조각은 한 덩이).
  const chunks: string[] = [''];
  for (const p of splitNumberRuns(raw)) {
    if (p.run) { chunks[chunks.length - 1] += p.text; continue; }
    p.text.split(/([ \t]+)/).forEach((t, j) => { if (j % 2) chunks.push(''); else chunks[chunks.length - 1] += t; });
  }
  for (let j = chunks.length - 1; j >= 0; j--) if (!chunks[j]) chunks.splice(j, 1);
  const longest = Math.max(...chunks.map((c) => valueWidth(c, 1)));
  const px = Math.max(1, Math.min(floorPx, Math.floor(colW / Math.max(longest, 1e-6))));
  const lines: string[] = [];
  for (const c of chunks) {
    const cur = lines[lines.length - 1];
    if (cur !== undefined && valueWidth(`${cur} ${c}`, px) <= colW) lines[lines.length - 1] = `${cur} ${c}`;
    else lines.push(c);
  }
  return { fit: true, lines, fontPx: px };
}
